import {
  FacebookBookingRequestEvent,
  FacebookInitiateCheckoutEvent,
  FacebookPurchaseEvent,
} from './facebook';
import {
  FirebaseBeginCheckoutEvent,
  FirebaseBookingRequestEvent,
  FirebaseOrderItem,
  FirebasePurchaseEvent,
} from './firebase';
import { logError } from '../util/log';
import {
  EXTRA_LINE_ITEMS,
  LINE_ITEMS,
  LINE_ITEM_DAY,
  LINE_ITEM_NIGHT,
  LINE_ITEM_UNITS,
} from '../util/types';

// Fallback name for the stay line item when no listing title can be resolved.
export const FALLBACK_LISTING_ITEM_NAME = 'Stellplatz-Buchung';

// Only the actual stay positions carry the listing name — LINE_ITEMS also
// contains commissions/vouchers, which must keep falling back to their item_id.
const STAY_LINE_ITEMS = [LINE_ITEM_NIGHT, LINE_ITEM_DAY, LINE_ITEM_UNITS];

const createGoogleItems = (lineItems, processName, processVersion, listingTitle) => {
  return lineItems.reduce((acc, lineItem) => {
    if (lineItem.includeFor.includes('customer')) {
      const isStayLineItem = STAY_LINE_ITEMS.includes(lineItem.code);
      acc.push(
        FirebaseOrderItem.withItemId(lineItem.code)
          // Only the stay line item gets a readable name; extras and custom items
          // stay nameless so GA4 falls back to their item_id.
          .withItemName(isStayLineItem ? listingTitle || FALLBACK_LISTING_ITEM_NAME : null)
          .withPrice(lineItem.lineTotal.amount === 0 ? 0 : lineItem.lineTotal.amount / 100)
          .withQuantity(lineItem.quantity ? lineItem.quantity : 1)
          .withCurrency(lineItem.lineTotal.currency.toLowerCase())
          .withItemCategory(
            LINE_ITEMS.includes(lineItem.code)
              ? 'line_item'
              : EXTRA_LINE_ITEMS.includes(lineItem.code)
                ? 'extra_line_item'
                : 'custom_line_item',
          )
          .withItemCategory2(processName)
          .withItemCategory3(processVersion)
          .make(),
      );
    }
    return acc;
  }, []);
};

const createFacebookItems = (lineItems) => {
  return lineItems.reduce((acc, lineItem) => {
    if (lineItem.includeFor.includes('customer')) {
      acc.push({
        id: lineItem.code,
        price: lineItem.lineTotal.amount === 0 ? 0 : lineItem.lineTotal.amount / 100,
        quantity: lineItem.quantity ? lineItem.quantity : 1,
        currency: lineItem.lineTotal.currency.toLowerCase(),
        category: LINE_ITEMS.includes(lineItem.code)
          ? 'line_item'
          : EXTRA_LINE_ITEMS.includes(lineItem.code)
            ? 'extra_line_item'
            : 'custom_line_item',
      });
    }
    return acc;
  }, []);
};

export const trackPurchase = (transaction, currentUser, listingTitle) => {
  try {
    const { lineItems, processName, processVersion, payinTotal, metadata } = transaction.attributes;
    const voucherCode = metadata.voucher?.code;
    const googleItems = createGoogleItems(
      lineItems,
      processName,
      processVersion,
      listingTitle || transaction.listing?.attributes?.title,
    );
    FirebasePurchaseEvent.withTransactionId(transaction.id.uuid)
      .withCurrency(payinTotal.currency.toLowerCase())
      .withValue(payinTotal.amount / 100)
      .withCoupon(voucherCode)
      .withItems(googleItems)
      .logEvent();
    const faceBookItems = createFacebookItems(lineItems);
    FacebookPurchaseEvent.withCurrentUserUUID(currentUser?.id?.uuid)
      .withEventID(transaction.id.uuid)
      .withValue(payinTotal.amount / 100)
      .withCurrency(payinTotal.currency.toLowerCase())
      .withContents(faceBookItems)
      .withNumItems(faceBookItems.length)
      .logEvent();
  } catch (e) {
    logError(e, 'track-purchase-failed');
  }
};

export const trackInitPurchase = (transaction, orderParams, currentUser, listingTitle) => {
  try {
    const { lineItems, processName, processVersion, payinTotal } = transaction.attributes;
    const googleItems = createGoogleItems(
      lineItems,
      processName,
      processVersion,
      listingTitle || transaction.listing?.attributes?.title,
    );
    FirebaseBeginCheckoutEvent.withValue(payinTotal.amount > 0 ? payinTotal.amount / 100 : 0)
      .withCurrency(payinTotal.currency.toLowerCase())
      .withCoupon(orderParams.bookingData?.voucher?.code)
      .withItems(googleItems)
      .logEvent();
    const facebookItems = createFacebookItems(lineItems);
    FacebookInitiateCheckoutEvent.withCurrentUserUUID(currentUser?.id?.uuid)
      .withValue(payinTotal.amount > 0 ? payinTotal.amount / 100 : 0)
      .withCurrency(payinTotal.currency.toLowerCase())
      .withContents(facebookItems)
      .logEvent();
  } catch (e) {
    logError(e, 'track-init-purchase-failed');
  }
};

const PURCHASE_TRACKED_PREFIX = 'purchase_tracked_';

/**
 * Purchase mit Dedup-Guard für die OrderDetailsPage: Router-State übersteht
 * keinen Reload, sessionStorage fängt Remounts/Zurück-Navigation in derselben
 * Session ab. eventID/transaction_id bleibt die TX-UUID (Plattform-Dedup).
 */
export const trackPurchaseOnce = (transaction, currentUser) => {
  if (typeof window === 'undefined') return;
  const key = `${PURCHASE_TRACKED_PREFIX}${transaction.id.uuid}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
  } catch (e) {
    // sessionStorage kann fehlen (Safari private mode) — dann ohne Guard feuern,
    // Plattform-Dedup über eventID/transaction_id greift trotzdem.
  }
  trackPurchase(transaction, currentUser);
};

export const trackBookingRequest = (transaction, currentUser, listingTitle) => {
  try {
    const { lineItems, processName, processVersion, payinTotal, metadata } = transaction.attributes;
    const value = payinTotal.amount / 100;
    const currency = payinTotal.currency.toLowerCase();
    FirebaseBookingRequestEvent.withTransactionId(transaction.id.uuid)
      .withValue(value)
      .withCurrency(currency)
      .withCoupon(metadata?.voucher?.code)
      .withItems(
        createGoogleItems(
          lineItems,
          processName,
          processVersion,
          listingTitle || transaction.listing?.attributes?.title,
        ),
      )
      .logEvent();
    const contents = createFacebookItems(lineItems);
    FacebookBookingRequestEvent.withCurrentUserUUID(currentUser?.id?.uuid)
      .withEventID(transaction.id.uuid)
      .withValue(value)
      .withCurrency(currency)
      .withContents(contents)
      .withNumItems(contents.length)
      .logEvent();
  } catch (e) {
    logError(e, 'track-booking-request-failed');
  }
};
